import React, { useState } from "react";
import Panel from "@/components/Panel";
import LoadingSpinner from "@/components/LoadingSpinner";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useMcpServers, { McpServerConfig } from "@/hooks/useMcpServers";
import McpServerModal from "./McpServerModal";
import McpServerRow from "./McpServerRow";
import useApi from "@/hooks/useApi";

export default function McpServers() {
  const { items, loading, setItems } = useMcpServers();
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<McpServerConfig | null>(null);
  const api = useApi();

  function openAdd() {
    setEditTarget(null);
    setShowModal(true);
  }

  function openEdit(config: McpServerConfig) {
    setEditTarget(config);
    setShowModal(true);
  }

  function handleSaved(saved: McpServerConfig) {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
    setShowModal(false);
  }

  async function handleDelete(id: number) {
    try {
      await api.admin.delete(`McpServers/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch {
      // errors surfaced in McpServerRow
    }
  }

  async function handleDiscover(config: McpServerConfig) {
    try {
      const resp = await api.admin.post(`McpServers/${config.id}/discover`, {});
      if (resp.ok) {
        const updated = await resp.json() as McpServerConfig;
        setItems((prev) =>
          prev.map((x) => (x.id === updated.id ? updated : x))
        );
      }
    } catch {
      // errors surfaced in McpServerRow
    }
  }

  return (
    <Panel title="MCP Servers">
      <div className="p-4">
        <p className="text-sm text-slate-600 mb-4">
          MCP (Model Context Protocol) servers give AI workflow nodes access to
          external tools like NetSuite — with full awareness of the current
          conversation's context. When a variable is already known (e.g. an order
          number from a pre-chat form), the AI will confirm it with the visitor
          rather than asking again.
        </p>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {items.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">
                No MCP servers configured yet. Add one to enable AI tool calling
                in workflow nodes.
              </p>
            ) : (
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                <table className="w-full bg-slate-50 text-sm">
                  <thead>
                    <tr>
                      <th className="border-b p-2 text-left">Name</th>
                      <th className="border-b p-2 text-left">URL</th>
                      <th className="border-b p-2 text-left">Tools</th>
                      <th className="border-b p-2 text-left">Status</th>
                      <th className="border-b p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((config) => (
                      <McpServerRow
                        key={config.id}
                        config={config}
                        onEdit={() => openEdit(config)}
                        onDelete={() => handleDelete(config.id)}
                        onDiscover={() => handleDiscover(config)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4">
              <PrimaryBlueButton label="Add MCP Server" onClick={openAdd} />
            </div>
          </>
        )}
      </div>

      {showModal && (
        <McpServerModal
          existing={editTarget}
          onSaved={handleSaved}
          onClose={() => setShowModal(false)}
        />
      )}
    </Panel>
  );
}
